/**
 * Auto Image Finder Service
 * Handles API calls to find images automatically based on toy information
 */

const AUTO_IMAGE_FINDER_URL = process.env.REACT_APP_AUTO_IMAGE_FINDER_URL;

export const autoImageFinderService = {
  /**
   * Find images for a toy based on its details
   * @param {Object} toyData - The toy data containing name, brand, code, etc.
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} Array of image URLs found
   */
  async findImages(toyData, userId) {
    try {
      if (!AUTO_IMAGE_FINDER_URL) {
        throw new Error('Auto image finder URL is not configured');
      }

      if (!toyData) {
        throw new Error('Toy data is required');
      }

      if (!userId) {
        throw new Error('User ID is required');
      }

      // Prepare the request payload matching the API specification
      const payload = {
        userId: userId,
        type: 'toy',
        brand: toyData.brand || '',
        name: toyData.name || '',
        code: toyData.code || ''
      };

      console.log('Auto Image Finder - Request payload:', payload);

      // Make the API call
      const response = await fetch(AUTO_IMAGE_FINDER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Auto Image Finder - Response:', result);

      // Handle the specific API response format: array of objects with url field
      if (Array.isArray(result)) {
        // Extract URLs from the response array
        const urls = result.map(item => item.url).filter(url => url);
        console.log('Auto Image Finder - Extracted URLs:', urls);
        return urls;
      } else if (result.images && Array.isArray(result.images)) {
        return result.images;
      } else if (result.imageUrls && Array.isArray(result.imageUrls)) {
        return result.imageUrls;
      } else if (result.url) {
        return [result.url];
      } else if (typeof result === 'string') {
        return [result];
      } else {
        console.warn('Auto Image Finder - Unexpected response format:', result);
        return [];
      }
    } catch (error) {
      console.error('Auto Image Finder - Error:', error);
      throw error;
    }
  },

  /**
   * Find the best single image for a toy
   * @param {Object} toyData - The toy data containing name, brand, code, etc.
   * @param {string} userId - The user ID
   * @returns {Promise<string|null>} The best image URL or null if none found
   */
  async findBestImage(toyData, userId) {
    try {
      const images = await this.findImages(toyData, userId);
      
      if (images && images.length > 0) {
        // Return the first image as the "best" one
        // In the future, this could be enhanced with image quality scoring
        return images[0];
      }
      
      return null;
    } catch (error) {
      console.error('Auto Image Finder - Error finding best image:', error);
      throw error;
    }
  }
};

export default autoImageFinderService;
